import { useState, useEffect } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Receipt, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/contexts/I18nContext';

interface Driver {
  id: string;
  name: string;
  phone?: string;
}

interface GenerateCodSettlementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function GenerateCodSettlementModal({
  open,
  onOpenChange,
  onSuccess,
}: GenerateCodSettlementModalProps) {
  const { t } = useI18n();
  const { get } = useApi();
  const { post, loading } = useApi({ showSuccessToast: false, showErrorToast: false });
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loadingDrivers, setLoadingDrivers] = useState(false);

  useEffect(() => {
    if (open) {
      fetchDrivers();
      setSelectedDriverId('');
      setNotes('');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchDrivers = async () => {
    setLoadingDrivers(true);
    try {
      const result: any = await get('/api/v1/dashboard/drivers');
      const data = result?.success !== undefined ? result?.data : result;
      const list = data?.data ?? data?.list ?? data?.drivers ?? (Array.isArray(data) ? data : []);
      setDrivers(
        list.map((d: any) => ({
          id: d.id,
          name: d.user?.name ?? d.name ?? d.id,
          phone: d.user?.phone ?? d.phone,
        }))
      );
    } catch {
      toast.error('فشل في تحميل قائمة السائقين');
    } finally {
      setLoadingDrivers(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedDriverId) {
      setError('يرجى اختيار سائق');
      return;
    }

    try {
      await post(`/api/v1/driver-cod-settlements/${selectedDriverId}/generate`, {
        notes: notes.trim() || undefined,
      });
      toast.success(t.financials.settlement_generated);
      onSuccess();
      onOpenChange(false);
    } catch (err: any) {
      const msg = err?.message ?? 'فشل في إنشاء التسوية';
      setError(msg);
      toast.error(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]" dir="rtl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Receipt className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle>{t.financials.generate_cod_settlement}</DialogTitle>
              <DialogDescription className="text-xs">
                سيتم جمع جميع الشحنات المسلّمة غير المسوّاة للسائق المختار
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="driver">
              السائق <span className="text-destructive">*</span>
            </Label>
            {loadingDrivers ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                جاري تحميل السائقين...
              </div>
            ) : (
              <Select
                value={selectedDriverId}
                onValueChange={setSelectedDriverId}
                disabled={loading}
              >
                <SelectTrigger id="driver">
                  <SelectValue placeholder="اختر سائقاً..." />
                </SelectTrigger>
                <SelectContent>
                  {drivers.length === 0 ? (
                    <SelectItem value="_empty" disabled>
                      لا يوجد سائقون
                    </SelectItem>
                  ) : (
                    drivers.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        <span>{d.name}</span>
                        {d.phone && (
                          <span className="text-muted-foreground text-xs me-2">{d.phone}</span>
                        )}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">ملاحظات (اختياري)</Label>
            <Textarea
              id="notes"
              placeholder="أضف ملاحظة توضيحية..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
              rows={3}
              className="resize-none"
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter className="gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              إلغاء
            </Button>
            <Button type="submit" disabled={loading || !selectedDriverId} className="gap-2">
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  جاري الإنشاء...
                </>
              ) : (
                <>
                  <Receipt className="h-4 w-4" />
                  إنشاء التسوية
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
